/* eslint-disable max-len */
import React, { useEffect, useRef, useState } from 'react'
import type { TFunction } from 'i18next'
import * as echarts from 'echarts'
import styled from 'styled-components'
import { Icon } from '../../../atoms/Icon'
import { FilterChip } from '../../../molecules/FilterDisplay'
import { InfoTooltip } from '../../../InfoTooltip'
import { TooltipMetricExplanation } from '../../../TooltipMetricExplanation'
import { Badge } from '../../../atoms/Badge'
import { Loading } from '../../../atoms/Loading'
import { FilterSelect } from '../../../molecules/FilterPanel/FilterFormComponents'
import {
  Grid,
  Card,
  CardHeader,
  CardTitle,
  MetricGrid,
  MetricRow,
  MetricLabel,
  MetricValue,
  MetricHighlight,
  ChartContainer,
} from '../BuildingCharacteristics.styles'
import type { NeighborhoodAnalysis } from '@popety-io/types'
import { translateGwrValue } from '../../../../utils/gwrTranslations'

interface NeighborhoodContextTabProps {
  building: any
  t: TFunction
  onFetchNeighborhoodAnalysis?: (
    buildingId: string,
    radius: number
  ) => Promise<NeighborhoodAnalysis>
  /** Pre-fetched neighborhood analysis data (optional, skips initial fetch if provided) */
  initialNeighborhoodAnalysis?: NeighborhoodAnalysis | null
  /** Callback when neighborhood analysis data changes (for saving to value_edited) */
  onNeighborhoodAnalysisChange?: (analysis: NeighborhoodAnalysis) => void
}

export const NeighborhoodContextTab: React.FC<NeighborhoodContextTabProps> = ({
  building,
  t,
  onFetchNeighborhoodAnalysis,
  initialNeighborhoodAnalysis,
  onNeighborhoodAnalysisChange,
}) => {
  const heightChartRef = useRef<HTMLDivElement>(null)
  const areaChartRef = useRef<HTMLDivElement>(null)
  const volumeChartRef = useRef<HTMLDivElement>(null)
  const ageDistributionChartRef = useRef<HTMLDivElement>(null)

  // State for neighborhood analysis data
  // Initialize with pre-fetched data if available
  const [neighborhoodAnalysis, setNeighborhoodAnalysis] =
    useState<NeighborhoodAnalysis | null>(initialNeighborhoodAnalysis || null)
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false)
  const [analysisError, setAnalysisError] = useState<string | null>(null)
  const [selectedRadius, setSelectedRadius] = useState<number>(200)
  // Track if we've ever fetched to know when to use initial data
  const [hasUserChangedRadius, setHasUserChangedRadius] = useState(false)

  // Fetch neighborhood analysis
  // Skip fetch if we have initial data and user hasn't changed the radius
  useEffect(() => {
    const fetchAnalysis = async () => {
      if (!building?.building_id || !onFetchNeighborhoodAnalysis) return

      // If we have initial data, default radius is 200m, and user hasn't changed radius, skip fetch
      if (
        initialNeighborhoodAnalysis &&
        selectedRadius === 200 &&
        !hasUserChangedRadius
      ) {
        // Use initial data, no need to fetch
        setNeighborhoodAnalysis(initialNeighborhoodAnalysis)
        return
      }

      setIsLoadingAnalysis(true)
      setAnalysisError(null)

      try {
        const analysis = await onFetchNeighborhoodAnalysis(
          building.building_id,
          selectedRadius
        )
        setNeighborhoodAnalysis(analysis)

        // Save to value_edited via callback (only when user changed radius)
        if (onNeighborhoodAnalysisChange && hasUserChangedRadius) {
          onNeighborhoodAnalysisChange(analysis)
        }
      } catch (error) {
        console.error('Failed to fetch neighborhood analysis:', error)
        setAnalysisError(
          error instanceof Error
            ? error.message
            : t('characteristics.neighborhood.errors.loadFailed')
        )
      } finally {
        setIsLoadingAnalysis(false)
      }
    }

    fetchAnalysis()
  }, [
    building?.building_id,
    selectedRadius,
    onFetchNeighborhoodAnalysis,
    initialNeighborhoodAnalysis,
    hasUserChangedRadius,
  ])

  // Calculate building conformity to quartier
  const calculateBuildingConformity = () => {
    console.log('=== BUILDING CONFORMITY DEBUG START ===')
    console.log('1. neighborhoodAnalysis exists?', !!neighborhoodAnalysis)
    console.log('2. building exists?', !!building)

    if (!neighborhoodAnalysis || !building) {
      console.log('FAILED: No neighborhood analysis or building data')
      return null
    }

    console.log(
      '3. Full neighborhoodAnalysis:',
      JSON.stringify(neighborhoodAnalysis, null, 2)
    )

    const quartier_profile =
      neighborhoodAnalysis.quartier_analysis?.quartier_profile
    const statistics = neighborhoodAnalysis.statistics

    console.log('4. quartier_profile exists?', !!quartier_profile)
    console.log('5. statistics exists?', !!statistics)
    console.log(
      '6. Full quartier_profile:',
      JSON.stringify(quartier_profile, null, 2)
    )
    console.log('7. Full statistics:', JSON.stringify(statistics, null, 2))

    // Check if we have minimum required data
    if (!quartier_profile || !statistics) {
      console.log('FAILED: Missing quartier_profile or statistics')
      return null
    }

    // Get building data
    const buildingYear = building.building_year || 0
    const buildingHeight = building.building_height || building.height || 0
    const buildingArea = building.area || building.building_area || 0
    const buildingFloors = building.floor_nb || 0
    const buildingCategory = building.category || ''

    console.log('8. Building data:', {
      buildingYear,
      buildingHeight,
      buildingArea,
      buildingFloors,
      buildingCategory,
    })

    console.log('9. Quartier profile data:', {
      typical_year: quartier_profile.typical_building_year,
      typical_height: quartier_profile.typical_height,
      typical_area: quartier_profile.typical_area,
      typical_category: quartier_profile.typical_category,
    })

    // 1. Temporal Conformity (40%)
    let temporalScore = 0
    if (buildingYear > 0 && quartier_profile.typical_building_year > 0) {
      const yearDiff = Math.abs(
        buildingYear - quartier_profile.typical_building_year
      )
      if (yearDiff <= 5) temporalScore = 100
      else if (yearDiff <= 10) temporalScore = 75
      else if (yearDiff <= 20) temporalScore = 50
      else if (yearDiff <= 30) temporalScore = 25
      else temporalScore = 0
    }

    // 2. Physical Conformity (40%) - average of height, area, floors
    const physicalScores: number[] = []

    // Height conformity
    if (
      buildingHeight > 0 &&
      quartier_profile.typical_height &&
      quartier_profile.typical_height > 0
    ) {
      const heightDiff =
        Math.abs(buildingHeight - quartier_profile.typical_height) /
        quartier_profile.typical_height
      if (heightDiff <= 0.2) physicalScores.push(100)
      else if (heightDiff <= 0.4)
        physicalScores.push(50 + (0.4 - heightDiff) * 250)
      else physicalScores.push(Math.max(0, 50 - (heightDiff - 0.4) * 100))
    }

    // Area conformity
    if (
      buildingArea > 0 &&
      quartier_profile.typical_area &&
      quartier_profile.typical_area > 0
    ) {
      const areaDiff =
        Math.abs(buildingArea - quartier_profile.typical_area) /
        quartier_profile.typical_area
      if (areaDiff <= 0.3) physicalScores.push(100)
      else if (areaDiff <= 0.6)
        physicalScores.push(50 + (0.6 - areaDiff) * 166.67)
      else physicalScores.push(Math.max(0, 50 - (areaDiff - 0.6) * 100))
    }

    // Floor conformity
    if (
      buildingFloors > 0 &&
      statistics.floor_count &&
      statistics.floor_count.mean > 0
    ) {
      const floorDiff = Math.abs(buildingFloors - statistics.floor_count.mean)
      if (floorDiff === 0) physicalScores.push(100)
      else if (floorDiff <= 1) physicalScores.push(75)
      else if (floorDiff <= 2) physicalScores.push(50)
      else physicalScores.push(Math.max(0, 50 - (floorDiff - 2) * 10))
    }

    const physicalScore =
      physicalScores.length > 0
        ? physicalScores.reduce((sum, score) => sum + score, 0) /
          physicalScores.length
        : 0

    // 3. Typological Conformity (20%)
    const typologicalScore =
      buildingCategory && quartier_profile.typical_category
        ? buildingCategory === quartier_profile.typical_category
          ? 100
          : 0
        : 0

    // Calculate weighted overall score
    const overallScore =
      temporalScore * 0.4 + physicalScore * 0.4 + typologicalScore * 0.2

    const result = {
      score: Math.round(overallScore * 10) / 10,
      temporal: Math.round(temporalScore * 10) / 10,
      physical: Math.round(physicalScore * 10) / 10,
      typological: Math.round(typologicalScore * 10) / 10,
    }

    console.log('10. SUCCESS! Building conformity result:', result)
    console.log('=== BUILDING CONFORMITY DEBUG END ===')

    return result
  }

  const buildingConformity = calculateBuildingConformity()

  // Building metrics
  const buildingHeight = building.building_height || building.height || 0
  const buildingArea = building.area || building.building_area || 0
  const buildingVolume = building.building_volume || 0

  // Neighbor metrics
  const neighborsHeightMean = building.neighbors_building_height_mean || 0
  const neighborsHeightMax = building.neighbors_building_height_max || 0
  const neighborsAreaMean = building.neighbors_building_area_mean || 0
  const neighborsAreaMax = building.neighbors_building_area_max || 0
  const neighborsVolumeMean = building.neighbors_building_volume_mean || 0
  const neighborsVolumeMax = building.neighbors_building_volume_max || 0
  const neighborsCount = building.neighbors_building_nb || 0

  // Create comparison charts
  useEffect(() => {
    // Height comparison chart
    if (
      heightChartRef.current &&
      buildingHeight > 0 &&
      neighborsHeightMean > 0
    ) {
      const chart = echarts.init(heightChartRef.current)
      const option = {
        animation: true,
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
        },
        grid: {
          left: '15%',
          right: '10%',
          bottom: '10%',
          top: '5%',
        },
        xAxis: {
          type: 'value',
          name: t('characteristics.neighborhood.chart.heightAxis'),
        },
        yAxis: {
          type: 'category',
          data: [
            t('characteristics.neighborhood.chart.thisBuilding'),
            t('characteristics.neighborhood.chart.neighborhoodAvg'),
            t('characteristics.neighborhood.chart.neighborhoodMax'),
          ],
        },
        series: [
          {
            type: 'bar',
            data: [
              {
                value: buildingHeight,
                itemStyle: { color: '#3b82f6' },
              },
              {
                value: neighborsHeightMean,
                itemStyle: { color: '#10b981' },
              },
              {
                value: neighborsHeightMax,
                itemStyle: { color: '#f59e0b' },
              },
            ],
            label: {
              show: true,
              position: 'right',
              formatter: '{c} m',
            },
          },
        ],
      }
      chart.setOption(option)

      const handleResize = () => chart.resize()
      window.addEventListener('resize', handleResize)
      return () => {
        window.removeEventListener('resize', handleResize)
        chart.dispose()
      }
    }
  }, [buildingHeight, neighborsHeightMean, neighborsHeightMax, t])

  useEffect(() => {
    // Area comparison chart
    if (areaChartRef.current && buildingArea > 0 && neighborsAreaMean > 0) {
      const chart = echarts.init(areaChartRef.current)
      const option = {
        animation: true,
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
        },
        grid: {
          left: '15%',
          right: '10%',
          bottom: '10%',
          top: '5%',
        },
        xAxis: {
          type: 'value',
          name: t('characteristics.neighborhood.chart.areaAxis'),
        },
        yAxis: {
          type: 'category',
          data: [
            t('characteristics.neighborhood.chart.thisBuilding'),
            t('characteristics.neighborhood.chart.neighborhoodAvg'),
            t('characteristics.neighborhood.chart.neighborhoodMax'),
          ],
        },
        series: [
          {
            type: 'bar',
            data: [
              {
                value: buildingArea,
                itemStyle: { color: '#3b82f6' },
              },
              {
                value: neighborsAreaMean,
                itemStyle: { color: '#10b981' },
              },
              {
                value: neighborsAreaMax,
                itemStyle: { color: '#f59e0b' },
              },
            ],
            label: {
              show: true,
              position: 'right',
              formatter: '{c} m²',
            },
          },
        ],
      }
      chart.setOption(option)

      const handleResize = () => chart.resize()
      window.addEventListener('resize', handleResize)
      return () => {
        window.removeEventListener('resize', handleResize)
        chart.dispose()
      }
    }
  }, [buildingArea, neighborsAreaMean, neighborsAreaMax, t])

  useEffect(() => {
    // Volume comparison chart
    if (
      volumeChartRef.current &&
      buildingVolume > 0 &&
      neighborsVolumeMean > 0
    ) {
      const chart = echarts.init(volumeChartRef.current)
      const option = {
        animation: true,
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
        },
        grid: {
          left: '15%',
          right: '10%',
          bottom: '10%',
          top: '5%',
        },
        xAxis: {
          type: 'value',
          name: t('characteristics.neighborhood.chart.volumeAxis'),
        },
        yAxis: {
          type: 'category',
          data: [
            t('characteristics.neighborhood.chart.thisBuilding'),
            t('characteristics.neighborhood.chart.neighborhoodAvg'),
            t('characteristics.neighborhood.chart.neighborhoodMax'),
          ],
        },
        series: [
          {
            type: 'bar',
            data: [
              {
                value: buildingVolume,
                itemStyle: { color: '#3b82f6' },
              },
              {
                value: neighborsVolumeMean,
                itemStyle: { color: '#10b981' },
              },
              {
                value: neighborsVolumeMax,
                itemStyle: { color: '#f59e0b' },
              },
            ],
            label: {
              show: true,
              position: 'right',
              formatter: (params: any) =>
                `${Math.round(params.value).toLocaleString()} m³`,
            },
          },
        ],
      }
      chart.setOption(option)

      const handleResize = () => chart.resize()
      window.addEventListener('resize', handleResize)
      return () => {
        window.removeEventListener('resize', handleResize)
        chart.dispose()
      }
    }
  }, [buildingVolume, neighborsVolumeMean, neighborsVolumeMax, t])

  // Age distribution chart for neighborhood analysis
  useEffect(() => {
    if (
      ageDistributionChartRef.current &&
      neighborhoodAnalysis?.statistics?.age_distribution
    ) {
      const chart = echarts.init(ageDistributionChartRef.current)
      const ageData = neighborhoodAnalysis.statistics.age_distribution

      const option = {
        animation: true,
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
        },
        grid: {
          left: '10%',
          right: '10%',
          bottom: '15%',
          top: '10%',
        },
        xAxis: {
          type: 'category',
          data: [
            t('characteristics.neighborhood.ageRanges.pre1919'),
            t('characteristics.neighborhood.ageRanges.1919_1945'),
            t('characteristics.neighborhood.ageRanges.1946_1960'),
            t('characteristics.neighborhood.ageRanges.1961_1970'),
            t('characteristics.neighborhood.ageRanges.1971_1980'),
            t('characteristics.neighborhood.ageRanges.1981_1990'),
            t('characteristics.neighborhood.ageRanges.1991_2000'),
            t('characteristics.neighborhood.ageRanges.2001_2010'),
            t('characteristics.neighborhood.ageRanges.2011_2020'),
            t('characteristics.neighborhood.ageRanges.post2020'),
          ],
          axisLabel: {
            rotate: 45,
            fontSize: 10,
          },
        },
        yAxis: {
          type: 'value',
          name: t('characteristics.neighborhood.chart.buildingsAxis'),
        },
        series: [
          {
            type: 'bar',
            data: [
              ageData.pre_1919,
              ageData['1919_1945'],
              ageData['1946_1960'],
              ageData['1961_1970'],
              ageData['1971_1980'],
              ageData['1981_1990'],
              ageData['1991_2000'],
              ageData['2001_2010'],
              ageData['2011_2020'],
              ageData.post_2020,
            ],
            itemStyle: {
              color: '#3b82f6',
            },
            label: {
              show: true,
              position: 'top',
              fontSize: 10,
            },
          },
        ],
      }
      chart.setOption(option)

      const handleResize = () => chart.resize()
      window.addEventListener('resize', handleResize)
      return () => {
        window.removeEventListener('resize', handleResize)
        chart.dispose()
      }
    }
  }, [neighborhoodAnalysis, t])

  const hasNeighborData =
    neighborsHeightMean > 0 || neighborsAreaMean > 0 || neighborsVolumeMean > 0

  const getComparisonText = (value: number, avgValue: number) => {
    const diff = ((value - avgValue) / avgValue) * 100
    if (Math.abs(diff) < 10)
      return t('characteristics.neighborhood.comparison.similar')
    if (diff > 0)
      return t('characteristics.neighborhood.comparison.aboveAverage', {
        percent: Math.round(diff),
      })
    return t('characteristics.neighborhood.comparison.belowAverage', {
      percent: Math.round(Math.abs(diff)),
    })
  }

  // Show "no data" message only if both old and new data are missing
  if (
    !hasNeighborData &&
    !neighborhoodAnalysis &&
    !isLoadingAnalysis &&
    !analysisError
  ) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>
            <Icon name="map-pin" size="small" />
            {t('characteristics.neighborhood.title')}
          </CardTitle>
        </CardHeader>
        <div
          style={{
            padding: '60px 20px',
            textAlign: 'center',
            color: '#999',
            fontSize: '14px',
          }}
        >
          {t('characteristics.neighborhood.noData')}
        </div>
      </Card>
    )
  }

  return (
    <>
      {/* Comparative Charts - Only show if old neighbor data exists */}
      {hasNeighborData && (
        <>
          <Grid $columns={1}>
            {/* Height Comparison */}
            {buildingHeight > 0 && neighborsHeightMean > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>
                    <Icon name="arrow-up" size="small" />
                    {t(
                      'characteristics.neighborhood.cards.heightComparison.title'
                    )}
                    <InfoTooltip>
                      <TooltipMetricExplanation
                        title={t(
                          'characteristics.neighborhood.cards.heightComparison.tooltip.title'
                        )}
                        shortDescription={t(
                          'characteristics.neighborhood.cards.heightComparison.tooltip.description'
                        )}
                        calculation={t(
                          'characteristics.neighborhood.cards.heightComparison.tooltip.calculation'
                        )}
                        importance={t(
                          'characteristics.neighborhood.cards.heightComparison.tooltip.importance'
                        )}
                        limitations={t(
                          'characteristics.neighborhood.cards.heightComparison.tooltip.limitations'
                        )}
                        source={t(
                          'characteristics.neighborhood.tooltips.source'
                        )}
                      />
                    </InfoTooltip>
                  </CardTitle>
                  <Badge variant="info">
                    {t('characteristics.neighborhood.neighboringBuildings', {
                      count: neighborsCount,
                    })}
                  </Badge>
                </CardHeader>
                <ChartContainer
                  ref={heightChartRef}
                  style={{ height: '200px' }}
                />
              </Card>
            )}

            {/* Area Comparison */}
            {buildingArea > 0 && neighborsAreaMean > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>
                    <Icon name="square" size="small" />
                    {t(
                      'characteristics.neighborhood.cards.areaComparison.title'
                    )}
                    <InfoTooltip>
                      <TooltipMetricExplanation
                        title={t(
                          'characteristics.neighborhood.cards.areaComparison.tooltip.title'
                        )}
                        shortDescription={t(
                          'characteristics.neighborhood.cards.areaComparison.tooltip.description'
                        )}
                        calculation={t(
                          'characteristics.neighborhood.cards.areaComparison.tooltip.calculation'
                        )}
                        importance={t(
                          'characteristics.neighborhood.cards.areaComparison.tooltip.importance'
                        )}
                        limitations={t(
                          'characteristics.neighborhood.cards.areaComparison.tooltip.limitations'
                        )}
                        source={t(
                          'characteristics.neighborhood.tooltips.source'
                        )}
                      />
                    </InfoTooltip>
                  </CardTitle>
                </CardHeader>
                <ChartContainer
                  ref={areaChartRef}
                  style={{ height: '200px' }}
                />
              </Card>
            )}

            {/* Volume Comparison */}
            {buildingVolume > 0 && neighborsVolumeMean > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>
                    <Icon name="box" size="small" />
                    {t(
                      'characteristics.neighborhood.cards.volumeComparison.title'
                    )}
                    <InfoTooltip>
                      <TooltipMetricExplanation
                        title={t(
                          'characteristics.neighborhood.cards.volumeComparison.tooltip.title'
                        )}
                        shortDescription={t(
                          'characteristics.neighborhood.cards.volumeComparison.tooltip.description'
                        )}
                        calculation={t(
                          'characteristics.neighborhood.cards.volumeComparison.tooltip.calculation'
                        )}
                        importance={t(
                          'characteristics.neighborhood.cards.volumeComparison.tooltip.importance'
                        )}
                        limitations={t(
                          'characteristics.neighborhood.cards.volumeComparison.tooltip.limitations'
                        )}
                        source={t(
                          'characteristics.neighborhood.tooltips.sourceCalculated'
                        )}
                      />
                    </InfoTooltip>
                  </CardTitle>
                </CardHeader>
                <ChartContainer
                  ref={volumeChartRef}
                  style={{ height: '200px' }}
                />
              </Card>
            )}
          </Grid>

          {/* Detailed Comparison Metrics */}
          <Grid $columns={2}>
            <Card>
              <CardHeader>
                <CardTitle>
                  <Icon name="bar-chart-2" size="small" />
                  {t(
                    'characteristics.neighborhood.cards.comparativeAnalysis.title'
                  )}
                </CardTitle>
              </CardHeader>
              <MetricGrid>
                {buildingHeight > 0 && neighborsHeightMean > 0 && (
                  <MetricRow>
                    <MetricLabel>
                      {t(
                        'characteristics.neighborhood.metrics.heightVsAverage.label'
                      )}
                      <InfoTooltip>
                        <TooltipMetricExplanation
                          title={t(
                            'characteristics.neighborhood.metrics.heightVsAverage.tooltip.title'
                          )}
                          shortDescription={t(
                            'characteristics.neighborhood.metrics.heightVsAverage.tooltip.description'
                          )}
                          calculation={t(
                            'characteristics.neighborhood.metrics.heightVsAverage.tooltip.calculation'
                          )}
                          importance={t(
                            'characteristics.neighborhood.metrics.heightVsAverage.tooltip.importance'
                          )}
                          source={t(
                            'characteristics.neighborhood.tooltips.sourceCalculated'
                          )}
                        />
                      </InfoTooltip>
                    </MetricLabel>
                    <MetricHighlight
                      $type={
                        buildingHeight > neighborsHeightMean * 1.2
                          ? 'info'
                          : buildingHeight < neighborsHeightMean * 0.8
                            ? 'warning'
                            : 'success'
                      }
                      $textAlign="right"
                    >
                      {getComparisonText(buildingHeight, neighborsHeightMean)}
                    </MetricHighlight>
                  </MetricRow>
                )}
                {buildingArea > 0 && neighborsAreaMean > 0 && (
                  <MetricRow>
                    <MetricLabel>
                      {t(
                        'characteristics.neighborhood.metrics.areaVsAverage.label'
                      )}
                      <InfoTooltip>
                        <TooltipMetricExplanation
                          title={t(
                            'characteristics.neighborhood.metrics.areaVsAverage.tooltip.title'
                          )}
                          shortDescription={t(
                            'characteristics.neighborhood.metrics.areaVsAverage.tooltip.description'
                          )}
                          calculation={t(
                            'characteristics.neighborhood.metrics.areaVsAverage.tooltip.calculation'
                          )}
                          importance={t(
                            'characteristics.neighborhood.metrics.areaVsAverage.tooltip.importance'
                          )}
                          source={t(
                            'characteristics.neighborhood.tooltips.sourceCalculated'
                          )}
                        />
                      </InfoTooltip>
                    </MetricLabel>
                    <MetricHighlight
                      $type={
                        buildingArea > neighborsAreaMean * 1.2
                          ? 'info'
                          : buildingArea < neighborsAreaMean * 0.8
                            ? 'warning'
                            : 'success'
                      }
                      $textAlign="right"
                    >
                      {getComparisonText(buildingArea, neighborsAreaMean)}
                    </MetricHighlight>
                  </MetricRow>
                )}
                {buildingVolume > 0 && neighborsVolumeMean > 0 && (
                  <MetricRow>
                    <MetricLabel>
                      {t(
                        'characteristics.neighborhood.metrics.volumeVsAverage.label'
                      )}
                      <InfoTooltip>
                        <TooltipMetricExplanation
                          title={t(
                            'characteristics.neighborhood.metrics.volumeVsAverage.tooltip.title'
                          )}
                          shortDescription={t(
                            'characteristics.neighborhood.metrics.volumeVsAverage.tooltip.description'
                          )}
                          calculation={t(
                            'characteristics.neighborhood.metrics.volumeVsAverage.tooltip.calculation'
                          )}
                          importance={t(
                            'characteristics.neighborhood.metrics.volumeVsAverage.tooltip.importance'
                          )}
                          source={t(
                            'characteristics.neighborhood.tooltips.sourceCalculated'
                          )}
                        />
                      </InfoTooltip>
                    </MetricLabel>
                    <MetricHighlight
                      $type={
                        buildingVolume > neighborsVolumeMean * 1.2
                          ? 'info'
                          : buildingVolume < neighborsVolumeMean * 0.8
                            ? 'warning'
                            : 'success'
                      }
                      $textAlign="right"
                    >
                      {getComparisonText(buildingVolume, neighborsVolumeMean)}
                    </MetricHighlight>
                  </MetricRow>
                )}
              </MetricGrid>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  <Icon name="map" size="small" />
                  {t('characteristics.neighborhood.title')}
                </CardTitle>
              </CardHeader>
              <MetricGrid>
                <MetricRow>
                  <MetricLabel>
                    {t(
                      'characteristics.neighborhood.metrics.neighboringBuildings.label'
                    )}
                    <InfoTooltip>
                      <TooltipMetricExplanation
                        title={t(
                          'characteristics.neighborhood.metrics.neighboringBuildings.tooltip.title'
                        )}
                        shortDescription={t(
                          'characteristics.neighborhood.metrics.neighboringBuildings.tooltip.description'
                        )}
                        calculation={t(
                          'characteristics.neighborhood.metrics.neighboringBuildings.tooltip.calculation'
                        )}
                        importance={t(
                          'characteristics.neighborhood.metrics.neighboringBuildings.tooltip.importance'
                        )}
                        source={t(
                          'characteristics.neighborhood.tooltips.source'
                        )}
                      />
                    </InfoTooltip>
                  </MetricLabel>
                  <MetricValue>
                    {neighborsCount || t('common.notAvailable')}
                  </MetricValue>
                </MetricRow>
                {building.building_density && (
                  <MetricRow>
                    <MetricLabel>
                      {t(
                        'characteristics.neighborhood.metrics.buildingDensity.label'
                      )}
                      <InfoTooltip>
                        <TooltipMetricExplanation
                          title={t(
                            'characteristics.neighborhood.metrics.buildingDensity.tooltip.title'
                          )}
                          shortDescription={t(
                            'characteristics.neighborhood.metrics.buildingDensity.tooltip.description'
                          )}
                          calculation={t(
                            'characteristics.neighborhood.metrics.buildingDensity.tooltip.calculation'
                          )}
                          importance={t(
                            'characteristics.neighborhood.metrics.buildingDensity.tooltip.importance'
                          )}
                          source={t(
                            'characteristics.neighborhood.tooltips.source'
                          )}
                        />
                      </InfoTooltip>
                    </MetricLabel>
                    <MetricValue>
                      {t(
                        'characteristics.neighborhood.metrics.buildingDensity.value',
                        { density: building.building_density.toFixed(2) }
                      )}
                    </MetricValue>
                  </MetricRow>
                )}
                {building.neighbors_building_year_mean && (
                  <MetricRow>
                    <MetricLabel>
                      {t(
                        'characteristics.neighborhood.metrics.avgNeighborAge.label'
                      )}
                      <InfoTooltip>
                        <TooltipMetricExplanation
                          title={t(
                            'characteristics.neighborhood.metrics.avgNeighborAge.tooltip.title'
                          )}
                          shortDescription={t(
                            'characteristics.neighborhood.metrics.avgNeighborAge.tooltip.description'
                          )}
                          calculation={t(
                            'characteristics.neighborhood.metrics.avgNeighborAge.tooltip.calculation'
                          )}
                          importance={t(
                            'characteristics.neighborhood.metrics.avgNeighborAge.tooltip.importance'
                          )}
                          source={t(
                            'characteristics.neighborhood.tooltips.sourceCalculated'
                          )}
                        />
                      </InfoTooltip>
                    </MetricLabel>
                    <MetricValue>
                      {t(
                        'characteristics.neighborhood.metrics.avgNeighborAge.value',
                        {
                          years:
                            new Date().getFullYear() -
                            Math.round(building.neighbors_building_year_mean),
                        }
                      )}
                    </MetricValue>
                  </MetricRow>
                )}
              </MetricGrid>
            </Card>
          </Grid>
        </>
      )}

      {/* Quartier Analysis Section */}
      {isLoadingAnalysis && (
        <Card>
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <Loading />
            <div style={{ marginTop: '16px', color: '#666', fontSize: '14px' }}>
              {t('characteristics.neighborhood.loading')}
            </div>
          </div>
        </Card>
      )}

      {analysisError && !isLoadingAnalysis && (
        <Card>
          <div
            style={{
              padding: '40px',
              textAlign: 'center',
              color: '#e74c3c',
              fontSize: '14px',
            }}
          >
            <Icon name="alert-circle" size="medium" />
            <div style={{ marginTop: '12px' }}>{analysisError}</div>
          </div>
        </Card>
      )}

      {neighborhoodAnalysis && !isLoadingAnalysis && (
        <>
          {/* Radius Selector */}
          <RadiusFilterBar>
            <RadiusFilters>
              <FilterChip active={true}>
                <Icon name="target" size="small" />
                {t('characteristics.neighborhood.radius.label')}
              </FilterChip>
              <FilterSelect
                value={selectedRadius.toString()}
                onChange={(value) => {
                  setHasUserChangedRadius(true)
                  setSelectedRadius(parseInt(value, 10))
                }}
                options={[
                  { value: '100', label: '100m' },
                  { value: '200', label: '200m' },
                  { value: '500', label: '500m' },
                ]}
              />
              <RadiusBadge>
                <Icon name="map-pin" size="small" />
                {t('characteristics.neighborhood.radius.value', {
                  radius: selectedRadius,
                })}
              </RadiusBadge>
            </RadiusFilters>
          </RadiusFilterBar>

          {/* Neighborhood & Building Analysis - Full Width */}
          <Card>
            <CardHeader>
              <CardTitle>
                <Icon name="home" size="small" />
                {t(
                  'characteristics.neighborhood.cards.neighborhoodAnalysis.title'
                )}
                <InfoTooltip>
                  <TooltipMetricExplanation
                    title={t(
                      'characteristics.neighborhood.cards.neighborhoodAnalysis.tooltip.title'
                    )}
                    shortDescription={t(
                      'characteristics.neighborhood.cards.neighborhoodAnalysis.tooltip.description'
                    )}
                    calculation={t(
                      'characteristics.neighborhood.cards.neighborhoodAnalysis.tooltip.calculation'
                    )}
                    importance={t(
                      'characteristics.neighborhood.cards.neighborhoodAnalysis.tooltip.importance'
                    )}
                    source={t(
                      'characteristics.neighborhood.tooltips.sourceCalculated'
                    )}
                  />
                </InfoTooltip>
              </CardTitle>
              {buildingConformity &&
                neighborhoodAnalysis.quartier_analysis.quartier_score >= 70 &&
                buildingConformity.score < 40 && (
                  <Badge variant="danger">
                    {t('characteristics.neighborhood.badges.buildingOutlier')}
                  </Badge>
                )}
            </CardHeader>
            <MetricGrid>
              <MetricRow>
                <MetricLabel>
                  {t(
                    'characteristics.neighborhood.metrics.neighborhoodCohesion'
                  )}
                </MetricLabel>
                <MetricHighlight
                  $type={
                    neighborhoodAnalysis.quartier_analysis.quartier_score >= 70
                      ? 'success'
                      : neighborhoodAnalysis.quartier_analysis.quartier_score >=
                          40
                        ? 'warning'
                        : 'danger'
                  }
                  $textAlign="right"
                >
                  {neighborhoodAnalysis?.quartier_analysis?.quartier_score?.toFixed(
                    1
                  )}
                  /100
                </MetricHighlight>
              </MetricRow>
              {buildingConformity && (
                <MetricRow>
                  <MetricLabel>
                    {t(
                      'characteristics.neighborhood.metrics.buildingConformity'
                    )}
                  </MetricLabel>
                  <MetricHighlight
                    $type={
                      buildingConformity.score >= 70
                        ? 'success'
                        : buildingConformity.score >= 40
                          ? 'warning'
                          : 'danger'
                    }
                    $textAlign="right"
                  >
                    {buildingConformity.score.toFixed(1)}/100
                  </MetricHighlight>
                </MetricRow>
              )}
              <MetricRow>
                <MetricLabel>
                  {t('characteristics.neighborhood.metrics.confidence')}
                </MetricLabel>
                <MetricValue style={{ textTransform: 'capitalize' }}>
                  {neighborhoodAnalysis.quartier_analysis.confidence}
                </MetricValue>
              </MetricRow>
              <MetricRow>
                <MetricLabel>
                  {t('characteristics.neighborhood.metrics.buildingsAnalyzed')}
                </MetricLabel>
                <MetricValue>{neighborhoodAnalysis.building_count}</MetricValue>
              </MetricRow>
            </MetricGrid>
            {buildingConformity &&
              neighborhoodAnalysis.quartier_analysis.quartier_score >= 70 &&
              buildingConformity.score < 40 && (
                <div
                  style={{
                    padding: '12px 16px',
                    marginTop: '12px',
                    backgroundColor: '#fff3cd',
                    borderLeft: '4px solid #ffc107',
                    fontSize: '13px',
                    color: '#856404',
                  }}
                >
                  <strong>
                    {t('characteristics.neighborhood.warnings.outlierTitle')}
                  </strong>{' '}
                  {t(
                    'characteristics.neighborhood.warnings.outlierDescription'
                  )}
                </div>
              )}
          </Card>

          {/* Cohesion Breakdown and Building Conformity Breakdown - Side by Side */}
          <Grid $columns={2}>
            <Card>
              <CardHeader>
                <CardTitle>
                  <Icon name="pie-chart" size="small" />
                  {t(
                    'characteristics.neighborhood.cards.cohesionBreakdown.title'
                  )}
                </CardTitle>
              </CardHeader>
              <MetricGrid>
                <MetricRow>
                  <MetricLabel>
                    {t('characteristics.neighborhood.cohesion.temporal.label')}
                    <InfoTooltip>
                      <TooltipMetricExplanation
                        title={t(
                          'characteristics.neighborhood.cohesion.temporal.tooltip.title'
                        )}
                        shortDescription={t(
                          'characteristics.neighborhood.cohesion.temporal.tooltip.description'
                        )}
                        calculation={t(
                          'characteristics.neighborhood.cohesion.temporal.tooltip.calculation'
                        )}
                      />
                    </InfoTooltip>
                  </MetricLabel>
                  <MetricValue>
                    {neighborhoodAnalysis.quartier_analysis.cohesion_breakdown.temporal.score.toFixed(
                      1
                    )}
                  </MetricValue>
                </MetricRow>
                <MetricRow>
                  <MetricLabel>
                    {t('characteristics.neighborhood.cohesion.physical.label')}
                    <InfoTooltip>
                      <TooltipMetricExplanation
                        title={t(
                          'characteristics.neighborhood.cohesion.physical.tooltip.title'
                        )}
                        shortDescription={t(
                          'characteristics.neighborhood.cohesion.physical.tooltip.description'
                        )}
                        calculation={t(
                          'characteristics.neighborhood.cohesion.physical.tooltip.calculation'
                        )}
                      />
                    </InfoTooltip>
                  </MetricLabel>
                  <MetricValue>
                    {neighborhoodAnalysis.quartier_analysis.cohesion_breakdown.physical.score.toFixed(
                      1
                    )}
                  </MetricValue>
                </MetricRow>
                <MetricRow>
                  <MetricLabel>
                    {t(
                      'characteristics.neighborhood.cohesion.typological.label'
                    )}
                    <InfoTooltip>
                      <TooltipMetricExplanation
                        title={t(
                          'characteristics.neighborhood.cohesion.typological.tooltip.title'
                        )}
                        shortDescription={t(
                          'characteristics.neighborhood.cohesion.typological.tooltip.description'
                        )}
                        calculation={t(
                          'characteristics.neighborhood.cohesion.typological.tooltip.calculation'
                        )}
                      />
                    </InfoTooltip>
                  </MetricLabel>
                  <MetricValue>
                    {neighborhoodAnalysis.quartier_analysis.cohesion_breakdown.typological.score.toFixed(
                      1
                    )}
                  </MetricValue>
                </MetricRow>
                <MetricRow>
                  <MetricLabel>
                    {t('characteristics.neighborhood.cohesion.density.label')}
                    <InfoTooltip>
                      <TooltipMetricExplanation
                        title={t(
                          'characteristics.neighborhood.cohesion.density.tooltip.title'
                        )}
                        shortDescription={t(
                          'characteristics.neighborhood.cohesion.density.tooltip.description'
                        )}
                        calculation={t(
                          'characteristics.neighborhood.cohesion.density.tooltip.calculation'
                        )}
                      />
                    </InfoTooltip>
                  </MetricLabel>
                  <MetricValue>
                    {neighborhoodAnalysis.quartier_analysis.cohesion_breakdown.density.score.toFixed(
                      1
                    )}
                  </MetricValue>
                </MetricRow>
              </MetricGrid>
            </Card>

            {/* Building Conformity Breakdown */}
            {buildingConformity && (
              <Card>
                <CardHeader>
                  <CardTitle>
                    <Icon name="check-circle" size="small" />
                    {t(
                      'characteristics.neighborhood.cards.conformityBreakdown.title'
                    )}
                    <InfoTooltip>
                      <TooltipMetricExplanation
                        title={t(
                          'characteristics.neighborhood.cards.conformityBreakdown.tooltip.title'
                        )}
                        shortDescription={t(
                          'characteristics.neighborhood.cards.conformityBreakdown.tooltip.description'
                        )}
                        calculation={t(
                          'characteristics.neighborhood.cards.conformityBreakdown.tooltip.calculation'
                        )}
                        importance={t(
                          'characteristics.neighborhood.cards.conformityBreakdown.tooltip.importance'
                        )}
                      />
                    </InfoTooltip>
                  </CardTitle>
                  <Badge
                    variant={
                      buildingConformity.score >= 70
                        ? 'success'
                        : buildingConformity.score >= 40
                          ? 'warning'
                          : 'danger'
                    }
                  >
                    {buildingConformity.score >= 70
                      ? t('characteristics.neighborhood.conformity.fitsWell')
                      : buildingConformity.score >= 40
                        ? t(
                            'characteristics.neighborhood.conformity.partialFit'
                          )
                        : t('characteristics.neighborhood.conformity.outlier')}
                  </Badge>
                </CardHeader>
                <MetricGrid>
                  <MetricRow>
                    <MetricLabel>
                      {t(
                        'characteristics.neighborhood.conformity.temporalMatch.label'
                      )}
                      <InfoTooltip>
                        <TooltipMetricExplanation
                          title={t(
                            'characteristics.neighborhood.conformity.temporalMatch.tooltip.title'
                          )}
                          shortDescription={t(
                            'characteristics.neighborhood.conformity.temporalMatch.tooltip.description'
                          )}
                          calculation={t(
                            'characteristics.neighborhood.conformity.temporalMatch.tooltip.calculation'
                          )}
                        />
                      </InfoTooltip>
                    </MetricLabel>
                    <MetricHighlight
                      $type={
                        buildingConformity.temporal >= 70
                          ? 'success'
                          : buildingConformity.temporal >= 40
                            ? 'warning'
                            : 'danger'
                      }
                      $textAlign="right"
                    >
                      {buildingConformity.temporal.toFixed(1)}
                    </MetricHighlight>
                  </MetricRow>
                  <MetricRow>
                    <MetricLabel>
                      {t(
                        'characteristics.neighborhood.conformity.physicalMatch.label'
                      )}
                      <InfoTooltip>
                        <TooltipMetricExplanation
                          title={t(
                            'characteristics.neighborhood.conformity.physicalMatch.tooltip.title'
                          )}
                          shortDescription={t(
                            'characteristics.neighborhood.conformity.physicalMatch.tooltip.description'
                          )}
                          calculation={t(
                            'characteristics.neighborhood.conformity.physicalMatch.tooltip.calculation'
                          )}
                        />
                      </InfoTooltip>
                    </MetricLabel>
                    <MetricHighlight
                      $type={
                        buildingConformity.physical >= 70
                          ? 'success'
                          : buildingConformity.physical >= 40
                            ? 'warning'
                            : 'danger'
                      }
                      $textAlign="right"
                    >
                      {buildingConformity.physical.toFixed(1)}
                    </MetricHighlight>
                  </MetricRow>
                  <MetricRow>
                    <MetricLabel>
                      {t(
                        'characteristics.neighborhood.conformity.typologicalMatch.label'
                      )}
                      <InfoTooltip>
                        <TooltipMetricExplanation
                          title={t(
                            'characteristics.neighborhood.conformity.typologicalMatch.tooltip.title'
                          )}
                          shortDescription={t(
                            'characteristics.neighborhood.conformity.typologicalMatch.tooltip.description'
                          )}
                          calculation={t(
                            'characteristics.neighborhood.conformity.typologicalMatch.tooltip.calculation'
                          )}
                        />
                      </InfoTooltip>
                    </MetricLabel>
                    <MetricHighlight
                      $type={
                        buildingConformity.typological >= 70
                          ? 'success'
                          : buildingConformity.typological >= 40
                            ? 'warning'
                            : 'danger'
                      }
                      $textAlign="right"
                    >
                      {buildingConformity.typological.toFixed(1)}
                    </MetricHighlight>
                  </MetricRow>
                </MetricGrid>
                {buildingConformity.score < 40 && (
                  <div
                    style={{
                      padding: '12px 16px',
                      marginTop: '12px',
                      backgroundColor: '#f8d7da',
                      borderLeft: '4px solid #dc3545',
                      fontSize: '13px',
                      color: '#721c24',
                    }}
                  >
                    <strong>
                      {t('characteristics.neighborhood.notes.title')}
                    </strong>{' '}
                    {t('characteristics.neighborhood.notes.differsByPattern')}
                    {buildingConformity.temporal < 40 &&
                      ` ${t('characteristics.neighborhood.notes.temporalMismatch')}`}
                    {buildingConformity.physical < 40 &&
                      ` ${t('characteristics.neighborhood.notes.physicalMismatch')}`}
                    {buildingConformity.typological < 40 &&
                      ` ${t('characteristics.neighborhood.notes.typologicalMismatch')}`}
                  </div>
                )}
              </Card>
            )}
          </Grid>

          {/* Quartier Profile */}
          <Card>
            <CardHeader>
              <CardTitle>
                <Icon name="info" size="small" />
                {t('characteristics.neighborhood.cards.quartierProfile.title')}
              </CardTitle>
            </CardHeader>
            <MetricGrid>
              <MetricRow>
                <MetricLabel>
                  {t('characteristics.neighborhood.profile.description.label')}
                  <InfoTooltip>
                    <TooltipMetricExplanation
                      title={t(
                        'characteristics.neighborhood.profile.description.tooltip.title'
                      )}
                      shortDescription={t(
                        'characteristics.neighborhood.profile.description.tooltip.description'
                      )}
                      calculation={t(
                        'characteristics.neighborhood.profile.description.tooltip.calculation'
                      )}
                      importance={t(
                        'characteristics.neighborhood.profile.description.tooltip.importance'
                      )}
                      source={t(
                        'characteristics.neighborhood.tooltips.sourceCalculated'
                      )}
                    />
                  </InfoTooltip>
                </MetricLabel>
                <MetricValue>
                  {(() => {
                    const desc = neighborhoodAnalysis.quartier_analysis.quartier_profile.description
                    if (!desc) return 'N/A'
                    // Description format: "1960s immeuble à trois logements et plus"
                    // Split at first space after decade pattern and translate the building class
                    const match = desc.match(/^(\d{4}s?\s+)(.+)$/)
                    if (match) {
                      return `${match[1]}${translateGwrValue(match[2], 'buildingClass', t)}`
                    }
                    return desc
                  })()}
                </MetricValue>
              </MetricRow>
              <MetricRow>
                <MetricLabel>
                  {t('characteristics.neighborhood.profile.typicalYear.label')}
                  <InfoTooltip>
                    <TooltipMetricExplanation
                      title={t(
                        'characteristics.neighborhood.profile.typicalYear.tooltip.title'
                      )}
                      shortDescription={t(
                        'characteristics.neighborhood.profile.typicalYear.tooltip.description'
                      )}
                      calculation={t(
                        'characteristics.neighborhood.profile.typicalYear.tooltip.calculation'
                      )}
                      importance={t(
                        'characteristics.neighborhood.profile.typicalYear.tooltip.importance'
                      )}
                      source={t('characteristics.neighborhood.tooltips.source')}
                    />
                  </InfoTooltip>
                </MetricLabel>
                <MetricValue>
                  {
                    neighborhoodAnalysis.quartier_analysis.quartier_profile
                      .typical_building_year
                  }
                </MetricValue>
              </MetricRow>
              <MetricRow>
                <MetricLabel>
                  {t(
                    'characteristics.neighborhood.profile.typicalHeight.label'
                  )}
                  <InfoTooltip>
                    <TooltipMetricExplanation
                      title={t(
                        'characteristics.neighborhood.profile.typicalHeight.tooltip.title'
                      )}
                      shortDescription={t(
                        'characteristics.neighborhood.profile.typicalHeight.tooltip.description'
                      )}
                      calculation={t(
                        'characteristics.neighborhood.profile.typicalHeight.tooltip.calculation'
                      )}
                      importance={t(
                        'characteristics.neighborhood.profile.typicalHeight.tooltip.importance'
                      )}
                      source={t('characteristics.neighborhood.tooltips.source')}
                    />
                  </InfoTooltip>
                </MetricLabel>
                <MetricValue>
                  {neighborhoodAnalysis.quartier_analysis.quartier_profile.typical_height.toFixed(
                    1
                  )}{' '}
                  m
                </MetricValue>
              </MetricRow>
              <MetricRow>
                <MetricLabel>
                  {t('characteristics.neighborhood.profile.typicalArea.label')}
                  <InfoTooltip>
                    <TooltipMetricExplanation
                      title={t(
                        'characteristics.neighborhood.profile.typicalArea.tooltip.title'
                      )}
                      shortDescription={t(
                        'characteristics.neighborhood.profile.typicalArea.tooltip.description'
                      )}
                      calculation={t(
                        'characteristics.neighborhood.profile.typicalArea.tooltip.calculation'
                      )}
                      importance={t(
                        'characteristics.neighborhood.profile.typicalArea.tooltip.importance'
                      )}
                      source={t('characteristics.neighborhood.tooltips.source')}
                    />
                  </InfoTooltip>
                </MetricLabel>
                <MetricValue>
                  {Math.round(
                    neighborhoodAnalysis.quartier_analysis.quartier_profile
                      .typical_area
                  )}{' '}
                  m²
                </MetricValue>
              </MetricRow>
              <MetricRow>
                <MetricLabel>
                  {t(
                    'characteristics.neighborhood.profile.typicalCategory.label'
                  )}
                  <InfoTooltip>
                    <TooltipMetricExplanation
                      title={t(
                        'characteristics.neighborhood.profile.typicalCategory.tooltip.title'
                      )}
                      shortDescription={t(
                        'characteristics.neighborhood.profile.typicalCategory.tooltip.description'
                      )}
                      calculation={t(
                        'characteristics.neighborhood.profile.typicalCategory.tooltip.calculation'
                      )}
                      importance={t(
                        'characteristics.neighborhood.profile.typicalCategory.tooltip.importance'
                      )}
                      source={t('characteristics.neighborhood.tooltips.source')}
                    />
                  </InfoTooltip>
                </MetricLabel>
                <MetricValue>
                  {translateGwrValue(
                    neighborhoodAnalysis.quartier_analysis.quartier_profile.typical_category,
                    'buildingClass',
                    t
                  )}
                </MetricValue>
              </MetricRow>
              <MetricRow>
                <MetricLabel>
                  {t(
                    'characteristics.neighborhood.profile.developmentPattern.label'
                  )}
                  <InfoTooltip>
                    <TooltipMetricExplanation
                      title={t(
                        'characteristics.neighborhood.profile.developmentPattern.tooltip.title'
                      )}
                      shortDescription={t(
                        'characteristics.neighborhood.profile.developmentPattern.tooltip.description'
                      )}
                      categories={[
                        {
                          label: t(
                            'characteristics.neighborhood.profile.developmentPattern.categories.planned.label'
                          ),
                          description: t(
                            'characteristics.neighborhood.profile.developmentPattern.categories.planned.description'
                          ),
                        },
                        {
                          label: t(
                            'characteristics.neighborhood.profile.developmentPattern.categories.mixed.label'
                          ),
                          description: t(
                            'characteristics.neighborhood.profile.developmentPattern.categories.mixed.description'
                          ),
                        },
                        {
                          label: t(
                            'characteristics.neighborhood.profile.developmentPattern.categories.organic.label'
                          ),
                          description: t(
                            'characteristics.neighborhood.profile.developmentPattern.categories.organic.description'
                          ),
                        },
                      ]}
                      importance={t(
                        'characteristics.neighborhood.profile.developmentPattern.tooltip.importance'
                      )}
                      source={t(
                        'characteristics.neighborhood.tooltips.sourceCalculated'
                      )}
                    />
                  </InfoTooltip>
                </MetricLabel>
                <MetricValue style={{ textTransform: 'capitalize' }}>
                  {
                    neighborhoodAnalysis.quartier_analysis.quartier_profile
                      .development_pattern
                  }
                </MetricValue>
              </MetricRow>
            </MetricGrid>
          </Card>

          {/* Age Distribution Chart */}
          {neighborhoodAnalysis.statistics?.age_distribution && (
            <Card>
              <CardHeader>
                <CardTitle>
                  <Icon name="bar-chart-2" size="small" />
                  {t(
                    'characteristics.neighborhood.cards.ageDistribution.title'
                  )}
                </CardTitle>
              </CardHeader>
              <ChartContainer
                ref={ageDistributionChartRef}
                style={{ height: '300px' }}
              />
            </Card>
          )}

          {/* Neighborhood Statistics */}
          <Grid $columns={3}>
            <Card>
              <CardHeader>
                <CardTitle>
                  <Icon name="trending-up" size="small" />
                  {t('characteristics.neighborhood.statistics.height.title')}
                </CardTitle>
              </CardHeader>
              <MetricGrid>
                <MetricRow>
                  <MetricLabel>
                    {t('characteristics.neighborhood.statistics.mean')}
                    <InfoTooltip>
                      <TooltipMetricExplanation
                        title={t(
                          'characteristics.neighborhood.statistics.height.mean.tooltip.title'
                        )}
                        shortDescription={t(
                          'characteristics.neighborhood.statistics.height.mean.tooltip.description'
                        )}
                        calculation={t(
                          'characteristics.neighborhood.statistics.height.mean.tooltip.calculation'
                        )}
                        importance={t(
                          'characteristics.neighborhood.statistics.height.mean.tooltip.importance'
                        )}
                        source={t(
                          'characteristics.neighborhood.tooltips.source'
                        )}
                      />
                    </InfoTooltip>
                  </MetricLabel>
                  <MetricValue>
                    {neighborhoodAnalysis.statistics.height.mean.toFixed(1)} m
                  </MetricValue>
                </MetricRow>
                <MetricRow>
                  <MetricLabel>
                    {t('characteristics.neighborhood.statistics.median')}
                    <InfoTooltip>
                      <TooltipMetricExplanation
                        title={t(
                          'characteristics.neighborhood.statistics.height.median.tooltip.title'
                        )}
                        shortDescription={t(
                          'characteristics.neighborhood.statistics.height.median.tooltip.description'
                        )}
                        calculation={t(
                          'characteristics.neighborhood.statistics.height.median.tooltip.calculation'
                        )}
                        importance={t(
                          'characteristics.neighborhood.statistics.height.median.tooltip.importance'
                        )}
                        source={t(
                          'characteristics.neighborhood.tooltips.source'
                        )}
                      />
                    </InfoTooltip>
                  </MetricLabel>
                  <MetricValue>
                    {neighborhoodAnalysis.statistics.height.median.toFixed(1)} m
                  </MetricValue>
                </MetricRow>
                <MetricRow>
                  <MetricLabel>
                    {t('characteristics.neighborhood.statistics.range')}
                    <InfoTooltip>
                      <TooltipMetricExplanation
                        title={t(
                          'characteristics.neighborhood.statistics.height.range.tooltip.title'
                        )}
                        shortDescription={t(
                          'characteristics.neighborhood.statistics.height.range.tooltip.description'
                        )}
                        calculation={t(
                          'characteristics.neighborhood.statistics.height.range.tooltip.calculation'
                        )}
                        importance={t(
                          'characteristics.neighborhood.statistics.height.range.tooltip.importance'
                        )}
                        source={t(
                          'characteristics.neighborhood.tooltips.source'
                        )}
                      />
                    </InfoTooltip>
                  </MetricLabel>
                  <MetricValue>
                    {neighborhoodAnalysis.statistics.height.min.toFixed(1)} -{' '}
                    {neighborhoodAnalysis.statistics.height.max.toFixed(1)} m
                  </MetricValue>
                </MetricRow>
              </MetricGrid>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  <Icon name="square" size="small" />
                  {t('characteristics.neighborhood.statistics.area.title')}
                </CardTitle>
              </CardHeader>
              <MetricGrid>
                <MetricRow>
                  <MetricLabel>
                    {t('characteristics.neighborhood.statistics.mean')}
                    <InfoTooltip>
                      <TooltipMetricExplanation
                        title={t(
                          'characteristics.neighborhood.statistics.area.mean.tooltip.title'
                        )}
                        shortDescription={t(
                          'characteristics.neighborhood.statistics.area.mean.tooltip.description'
                        )}
                        calculation={t(
                          'characteristics.neighborhood.statistics.area.mean.tooltip.calculation'
                        )}
                        importance={t(
                          'characteristics.neighborhood.statistics.area.mean.tooltip.importance'
                        )}
                        source={t(
                          'characteristics.neighborhood.tooltips.source'
                        )}
                      />
                    </InfoTooltip>
                  </MetricLabel>
                  <MetricValue>
                    {Math.round(neighborhoodAnalysis.statistics.area.mean)} m²
                  </MetricValue>
                </MetricRow>
                <MetricRow>
                  <MetricLabel>
                    {t('characteristics.neighborhood.statistics.median')}
                    <InfoTooltip>
                      <TooltipMetricExplanation
                        title={t(
                          'characteristics.neighborhood.statistics.area.median.tooltip.title'
                        )}
                        shortDescription={t(
                          'characteristics.neighborhood.statistics.area.median.tooltip.description'
                        )}
                        calculation={t(
                          'characteristics.neighborhood.statistics.area.median.tooltip.calculation'
                        )}
                        importance={t(
                          'characteristics.neighborhood.statistics.area.median.tooltip.importance'
                        )}
                        source={t(
                          'characteristics.neighborhood.tooltips.source'
                        )}
                      />
                    </InfoTooltip>
                  </MetricLabel>
                  <MetricValue>
                    {Math.round(neighborhoodAnalysis.statistics.area.median)} m²
                  </MetricValue>
                </MetricRow>
                <MetricRow>
                  <MetricLabel>
                    {t('characteristics.neighborhood.statistics.range')}
                    <InfoTooltip>
                      <TooltipMetricExplanation
                        title={t(
                          'characteristics.neighborhood.statistics.area.range.tooltip.title'
                        )}
                        shortDescription={t(
                          'characteristics.neighborhood.statistics.area.range.tooltip.description'
                        )}
                        calculation={t(
                          'characteristics.neighborhood.statistics.area.range.tooltip.calculation'
                        )}
                        importance={t(
                          'characteristics.neighborhood.statistics.area.range.tooltip.importance'
                        )}
                        source={t(
                          'characteristics.neighborhood.tooltips.source'
                        )}
                      />
                    </InfoTooltip>
                  </MetricLabel>
                  <MetricValue>
                    {Math.round(neighborhoodAnalysis.statistics.area.min)} -{' '}
                    {Math.round(neighborhoodAnalysis.statistics.area.max)} m²
                  </MetricValue>
                </MetricRow>
              </MetricGrid>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  <Icon name="layers" size="small" />
                  {t('characteristics.neighborhood.statistics.floor.title')}
                </CardTitle>
              </CardHeader>
              <MetricGrid>
                <MetricRow>
                  <MetricLabel>
                    {t('characteristics.neighborhood.statistics.mean')}
                    <InfoTooltip>
                      <TooltipMetricExplanation
                        title={t(
                          'characteristics.neighborhood.statistics.floor.mean.tooltip.title'
                        )}
                        shortDescription={t(
                          'characteristics.neighborhood.statistics.floor.mean.tooltip.description'
                        )}
                        calculation={t(
                          'characteristics.neighborhood.statistics.floor.mean.tooltip.calculation'
                        )}
                        importance={t(
                          'characteristics.neighborhood.statistics.floor.mean.tooltip.importance'
                        )}
                        source={t(
                          'characteristics.neighborhood.tooltips.source'
                        )}
                      />
                    </InfoTooltip>
                  </MetricLabel>
                  <MetricValue>
                    {t(
                      'characteristics.neighborhood.statistics.floor.floorsValue',
                      {
                        floors:
                          neighborhoodAnalysis.statistics.floor_count.mean.toFixed(
                            1
                          ),
                      }
                    )}
                  </MetricValue>
                </MetricRow>
                <MetricRow>
                  <MetricLabel>
                    {t('characteristics.neighborhood.statistics.median')}
                    <InfoTooltip>
                      <TooltipMetricExplanation
                        title={t(
                          'characteristics.neighborhood.statistics.floor.median.tooltip.title'
                        )}
                        shortDescription={t(
                          'characteristics.neighborhood.statistics.floor.median.tooltip.description'
                        )}
                        calculation={t(
                          'characteristics.neighborhood.statistics.floor.median.tooltip.calculation'
                        )}
                        importance={t(
                          'characteristics.neighborhood.statistics.floor.median.tooltip.importance'
                        )}
                        source={t(
                          'characteristics.neighborhood.tooltips.source'
                        )}
                      />
                    </InfoTooltip>
                  </MetricLabel>
                  <MetricValue>
                    {t(
                      'characteristics.neighborhood.statistics.floor.floorsValue',
                      {
                        floors: Math.round(
                          neighborhoodAnalysis.statistics.floor_count.median
                        ),
                      }
                    )}
                  </MetricValue>
                </MetricRow>
                <MetricRow>
                  <MetricLabel>
                    {t('characteristics.neighborhood.statistics.range')}
                    <InfoTooltip>
                      <TooltipMetricExplanation
                        title={t(
                          'characteristics.neighborhood.statistics.floor.range.tooltip.title'
                        )}
                        shortDescription={t(
                          'characteristics.neighborhood.statistics.floor.range.tooltip.description'
                        )}
                        calculation={t(
                          'characteristics.neighborhood.statistics.floor.range.tooltip.calculation'
                        )}
                        importance={t(
                          'characteristics.neighborhood.statistics.floor.range.tooltip.importance'
                        )}
                        source={t(
                          'characteristics.neighborhood.tooltips.source'
                        )}
                      />
                    </InfoTooltip>
                  </MetricLabel>
                  <MetricValue>
                    {t(
                      'characteristics.neighborhood.statistics.floor.floorsRange',
                      {
                        min: Math.round(
                          neighborhoodAnalysis.statistics.floor_count.min
                        ),
                        max: Math.round(
                          neighborhoodAnalysis.statistics.floor_count.max
                        ),
                      }
                    )}
                  </MetricValue>
                </MetricRow>
              </MetricGrid>
            </Card>
          </Grid>
        </>
      )}
    </>
  )
}

// Styled Components
const RadiusFilterBar = styled.div`
  padding: 20px 0px;
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  margin-bottom: 24px;
`

const RadiusFilters = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`

const RadiusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  color: ${({ theme }) => theme.colors.text.primary};
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;

  svg {
    width: 12px;
    height: 12px;
  }
`
